import { Children, useEffect, useRef, useState } from 'react';
import './App.css';
import IntakeForm from './components/IntakeForm';
import { ReactComponent as LogoMark } from './assets/logomark.svg';
import mikePortrait from './assets/mike-portrait.jpg';
import hero800 from './assets/hero-800.jpg';
import hero1200 from './assets/hero-1200.jpg';
import hero2400 from './assets/hero-2400.jpg';

// Contact values assembled at runtime to avoid plain-text scraping
const CONTACT_EMAIL = ['michaelmohanj', '@', 'gmail', '.com'].join('');
const CONTACT_PHONE = ['(785)', '\u202f', '608-7851'].join('');

const IconEmail = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="1.1em" height="1.1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="4" width="20" height="16" rx="2"/>
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
  </svg>
);

const IconPhone = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="1.1em" height="1.1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.15 11.5 19.79 19.79 0 0 1 1.09 2.18 2 2 0 0 1 3.09.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 8.09a16 16 0 0 0 5.5 5.5l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 20.92 14.92z"/>
  </svg>
);

const IconCopy = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="1.1em" height="1.1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect width="14" height="14" x="8" y="8" rx="2" ry="2"/>
    <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/>
  </svg>
);

const IconCheck = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="1.1em" height="1.1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 6 9 17l-5-5"/>
  </svg>
);

interface IconButtonProps {
  icon: React.ReactNode;
  activeIcon?: React.ReactNode;
  active?: boolean;
  onClick: () => void;
  ariaLabel: string;
  className?: string;
}

function IconButton({ icon, activeIcon, active = false, onClick, ariaLabel, className = '' }: IconButtonProps) {
  return (
    <button
      className={`icon-btn${active ? ' is-copied' : ''}${className ? ` ${className}` : ''}`}
      onClick={onClick}
      aria-label={ariaLabel}
      aria-pressed={active}
    >
      <span className="icon-btn__icon icon-btn__icon--default">{icon}</span>
      {activeIcon && (
        <span className="icon-btn__icon icon-btn__icon--active">{activeIcon}</span>
      )}
    </button>
  );
}

interface ContactRevealProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

function ContactReveal({ icon, label, value }: ContactRevealProps) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  function handleCopy() {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
    });
  }

  function handleTrigger() {
    setOpen(o => !o);
  }

  // Reset copied state whenever tooltip closes
  useEffect(() => {
    if (!open) setCopied(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handleOutside(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  return (
    <div className="contact-reveal" ref={wrapRef}>
      <button
        className={`cta cta--ghost cta--sm cta--with-leading-icon${open ? ' is-open' : ''}`}
        onClick={handleTrigger}
        aria-label={open ? `Hide ${label}` : `Reveal ${label}`}
        aria-expanded={open}
      >
        <span className="cta__icon">{icon}</span>
        <span className="cta__label">{label}</span>
      </button>
      {open && (
        <div className="contact-reveal__tooltip" role="status">
          <span className="contact-reveal__value">{value}</span>
          <IconButton
            icon={<IconCopy />}
            activeIcon={<IconCheck />}
            active={copied}
            onClick={handleCopy}
            ariaLabel={copied ? 'Copied!' : 'Copy to clipboard'}
          />
          {copied && <span className="contact-reveal__copied-msg">Copied!</span>}
        </div>
      )}
    </div>
  );
}

interface PrivacyModalProps {
  onClose: () => void;
}

function PrivacyModal({ onClose }: PrivacyModalProps) {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeRef.current?.focus();
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div
      className="modal-backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="privacy-modal-title"
    >
      <div className="modal-card" onClick={e => e.stopPropagation()}>
        <button
          ref={closeRef}
          className="modal-close"
          onClick={onClose}
          aria-label="Close privacy policy"
        >✕</button>
        <h2 id="privacy-modal-title">Privacy Policy</h2>
        <p className="privacy-updated">Last updated: May 2026</p>

        <h3>Information We Receive</h3>
        <p>
          This website is primarily informational. If you submit the Client Intake Form,
          we receive the information you provide, such as your name, email address,
          phone number, case type, and case description.
        </p>

        <h3>How We Use Intake Information</h3>
        <p>
          Intake information is used only to review your inquiry and communicate with
          you about potential legal services. It is not used for advertising, profiling,
          or automated decision-making.
        </p>

        <h3>No Ads or Analytics Tracking</h3>
        <p>
          We do not use advertising pixels, behavior tracking tools, or third-party
          analytics cookies on this website.
        </p>

        <h3>No Sale or Sharing to Third Parties</h3>
        <p>
          We do not sell personal information and do not share intake information with
          third parties for marketing or advertising purposes.
        </p>

        <h3>How Submissions Are Handled</h3>
        <p>
          Intake submissions are sent for attorney review and are not retained in a
          website database. Processing may involve standard service providers necessary
          to provide website and email functionality.
        </p>

        <h3>Confidentiality and Legal Relationship</h3>
        <p>
          Submitting information through this website does not create an attorney-client
          relationship. Please avoid sending highly sensitive information until
          representation is confirmed.
        </p>

        <h3>Questions or Requests</h3>
        <p>
          For privacy-related questions or requests, contact{' '}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        </p>
      </div>
    </div>
  );
}

interface CardCarouselProps {
  children: React.ReactNode;
  className?: string;
}

function CardCarousel({ children, className }: CardCarouselProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const trackRef = useRef<HTMLDivElement>(null);
  const items = Children.toArray(children);

  function handleScroll(e: React.UIEvent<HTMLDivElement>) {
    const el = e.currentTarget;
    const card = el.firstElementChild as HTMLElement | null;
    if (!card) return;
    const gap = parseFloat(getComputedStyle(el).gap) || 16;
    const idx = Math.round(el.scrollLeft / (card.offsetWidth + gap));
    setActiveIndex(Math.max(0, Math.min(idx, items.length - 1)));
  }

  function scrollToIndex(idx: number) {
    const el = trackRef.current;
    if (!el || !el.firstElementChild) return;
    const gap = parseFloat(getComputedStyle(el).gap) || 16;
    const firstChild = el.firstElementChild as HTMLElement;
    el.scrollTo({ left: idx * (firstChild.offsetWidth + gap), behavior: 'smooth' });
  }

  return (
    <div className="card-carousel">
      <div
        className={`card-carousel__track${className ? ` ${className}` : ''}`}
        ref={trackRef}
        onScroll={handleScroll}
      >
        {items}
      </div>
      <div className="card-carousel__dots" aria-hidden="true">
        {items.map((_, i) => (
          <button
            key={i}
            className={`card-carousel__dot${i === activeIndex ? ' is-active' : ''}`}
            onClick={() => scrollToIndex(i)}
            aria-label={`Go to item ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

function HomePage({ onOpenPrivacy }: { onOpenPrivacy: () => void }) {
  return (
    <>
      {/* Hero Section */}
      <section className="hero">
        <picture className="hero-bg" aria-hidden="true">
          <source media="(min-width: 1201px)" srcSet={hero2400} />
          <source media="(min-width: 801px)" srcSet={hero1200} />
          <img src={hero800} alt="" className="hero-bg__img" />
        </picture>
        <div className="hero-overlay" aria-hidden="true" />
        <div className="container hero-content">
          <h1>
            Privacy &amp; Cybersecurity Counsel
          </h1>
          <p className="tagline">
            The Law Office of Mike Mohan, PLLC advises businesses and individuals on data privacy,
            cybersecurity risk, and high-stakes disputes, including data breach response and privacy litigation.
          </p>
          <div className="cta-buttons">
            <a href="#intake" className="cta cta--primary">
              <span className="cta__label">Schedule a Consultation</span>
            </a>
          </div>
        </div>
      </section>
      <section id="about" className="about">
        <div className="container">
          <div className="about-content">
            <img
              src={mikePortrait}
              alt="Mike Mohan, Attorney"
              className="about-photo"
            />
            <div className="about-text">
              <h2>About Mike Mohan</h2>
              <p>
                Mike Mohan is an independent attorney specializing in privacy law,
                incident response, and plaintiff employment matters. With extensive
                experience protecting clients' rights in an increasingly digital world,
                Mike provides strategic counsel and vigorous representation.
              </p>
              <p>
                Whether you are facing a data breach, privacy violation, or employment
                dispute, Mike brings deep expertise and a commitment to achieving the
                best possible outcomes for his clients.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Credibility Section */}
      <section className="credibility">
        <div className="container">
          <h2>Proven courtroom experience in complex disputes:</h2>
          <CardCarousel className="credibility-grid">
            <div className="credibility-item credibility-item-stat">
              <span className="credibility-number">350+</span>
              <span className="credibility-label">Depositions</span>
            </div>
            <div className="credibility-item credibility-item-stat">
              <span className="credibility-number">100+</span>
              <span className="credibility-label">Arbitrations</span>
            </div>
            <div className="credibility-item credibility-item--narrative">First-Chair<br />Trial Experience</div>
            <div className="credibility-item credibility-item--narrative">Experience Litigating Privacy and FCRA Claims</div>
          </CardCarousel>
        </div>
      </section>

      {/* Practice Areas */}
      <section id="practice-areas" className="practice-areas">
        <div className="container">
          <h2>Practice Areas</h2>
          <CardCarousel className="areas-grid">
            <div className="area-card">
              <h3>Privacy &amp;<br /> Cybersecurity Counseling</h3>
              <p>
                Practical guidance on compliance, risk management, and data governance.
              </p>
            </div>
            <div className="area-card">
              <h3>Data Breach &amp;<br /> Incident Response</h3>
              <p>
                Rapid-response legal support when incidents occur, minimizing risk and exposure.
              </p>
            </div>
            <div className="area-card">
              <h3>Privacy &amp;<br /> Data Breach Litigation</h3>
              <p>
                Strategic representation in disputes involving data misuse, breaches,
                and regulatory exposure.
              </p>
            </div>
          </CardCarousel>
          <p className="positioning-statement">
            Mike Mohan brings extensive litigation experience to every matter, helping clients
            not only comply with the law, but prepare for and prevail in disputes.
          </p>
        </div>
      </section>

      {/* Intake Form */}
      <IntakeForm onOpenPrivacy={onOpenPrivacy} />

      {/* Contact Section */}
      <section id="contact" className="contact">
        <div className="container">
          <h2>Contact</h2>
          <p className="contact-intro">
            Schedule a consultation to discuss your legal needs.
          </p>
          <div className="contact-info">
            <ContactReveal icon={<IconEmail />} label="Email" value={CONTACT_EMAIL} />
            <ContactReveal icon={<IconPhone />} label="Phone" value={CONTACT_PHONE} />
          </div>
        </div>
      </section>
    </>
  );
}

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 0);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  function closeMenu() { setMenuOpen(false); }

  return (
    <div className="App" id="top">
      <nav className={`navbar${scrolled ? ' is-scrolled' : ''}`}>
        <div className="container">
          <h1 className="logo">
            <a href="#top">
              <span className="logo-lockup">
                <LogoMark className="logo-mark" aria-hidden="true" focusable="false" />
                <span>The Law Office<br /> of Mike Mohan</span>
              </span>
            </a>
          </h1>
          <ul className="nav-links">
            <li><a href="#about">About</a></li>
            <li><a href="#practice-areas">Practice Areas</a></li>
            <li><a href="#intake">Let's Chat</a></li>
            <li><a href="#contact">Contact</a></li>
          </ul>
          <button
            className={`nav-hamburger${menuOpen ? ' is-open' : ''}`}
            onClick={() => setMenuOpen(o => !o)}
            aria-expanded={menuOpen}
            aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
          >
            <span className="hamburger-bar" />
            <span className="hamburger-bar" />
            <span className="hamburger-bar" />
          </button>
        </div>
        {menuOpen && (
          <div className="nav-mobile-menu">
            <ul>
              <li><a href="#about" onClick={closeMenu}>About</a></li>
              <li><a href="#practice-areas" onClick={closeMenu}>Practice Areas</a></li>
              <li><a href="#intake" onClick={closeMenu}>Let's Chat</a></li>
              <li><a href="#contact" onClick={closeMenu}>Contact</a></li>
            </ul>
          </div>
        )}
      </nav>

      <main className="page-shell">
        <HomePage onOpenPrivacy={() => setPrivacyOpen(true)} />
      </main>

      <footer className="footer">
        <div className="container">
          <p>
            &copy; 2026 The Law Office of Mike Mohan, PLLC. All rights reserved.
            <button
              className="footer-privacy-link"
              onClick={() => setPrivacyOpen(true)}
            >Privacy Policy</button>
          </p>
          <p className="disclaimer">
            This website is for informational purposes only and does not constitute legal advice.
          </p>
        </div>
      </footer>

      {privacyOpen && <PrivacyModal onClose={() => setPrivacyOpen(false)} />}
    </div>
  );
}

export default App;
